import { Colors, DateInput, Text } from '@amelio/ui-library';
import { Controller } from 'react-hook-form';

import { CareerStepFormField } from './CareerStepFormField';
import { GROWTH_FIELD_BORDER } from './growthStyles';
import { HorizonYearInput } from './HorizonYearInput';

import type { CareerStepFormValues } from './careerStepValidation';
import type { Control } from 'react-hook-form';

import { useAmelioTranslations } from '@/hooks';
import { parseIsoDate, toDateInputLang, toIsoDate } from '@/utils/dateUtils';

interface CareerStepDateFieldsProps {
    control: Control<CareerStepFormValues>;
    kind: CareerStepFormValues['kind'];
    /** The start date currently typed, which bounds the end date. */
    startDate: CareerStepFormValues['startDate'];
    startDateError?: string;
    endDateError?: string;
    dataTestId: string;
}

/**
 * The dates of a career step, labelled by what the kind asks for.
 *
 * A target is a plan, not a period: it is aimed at a year, and it has no end -- reaching it is what
 * ends it. So it shows its horizon alone, as a year. The current position is still running, so it
 * gives its start alone. Only a past position, closed by definition, keeps both its days.
 */
export const CareerStepDateFields = ({
    control,
    kind,
    startDate,
    startDateError,
    endDateError,
    dataTestId,
}: CareerStepDateFieldsProps) => {
    const { t, i18n } = useAmelioTranslations();

    const horizonLabel = t('performanceProfile.growth.stepsEditor.horizonOptional');

    if (kind === 'Target') {
        return (
            <div className={'grid gap-4 md:grid-cols-2'}>
                <CareerStepFormField
                    label={<FieldLabel>{horizonLabel}</FieldLabel>}
                    error={startDateError}
                >
                    <Controller
                        name={'startDate'}
                        control={control}
                        render={({ field }) => (
                            <HorizonYearInput
                                value={field.value}
                                onChange={field.onChange}
                                dataTestId={`${dataTestId}-horizon`}
                            />
                        )}
                    />
                </CareerStepFormField>
            </div>
        );
    }

    const endDateLabel = t('performanceProfile.growth.stepsEditor.endDate');
    const startDateLabel = t('performanceProfile.growth.stepsEditor.startDate');
    const afterToday = { after: new Date() };
    const startDay = startDate ? parseIsoDate(startDate) : undefined;

    return (
        <div className={'grid gap-4 md:grid-cols-2'}>
            <CareerStepFormField
                label={<FieldLabel>{startDateLabel}</FieldLabel>}
                error={startDateError}
            >
                <Controller
                    name={'startDate'}
                    control={control}
                    render={({ field }) => (
                        <DateInput
                            value={field.value ?? undefined}
                            onChange={(date) => field.onChange(date ? toIsoDate(date) : null)}
                            disabledDates={kind === 'Past' ? afterToday : undefined}
                            lang={toDateInputLang(i18n.language)}
                            idleBorderClassName={GROWTH_FIELD_BORDER}
                            portalPanel
                            placeholder={startDateLabel}
                            ariaLabel={startDateLabel}
                            dataTestId={`${dataTestId}-start-date`}
                        />
                    )}
                />
            </CareerStepFormField>

            {kind === 'Past' && (
                <CareerStepFormField
                    label={<FieldLabel>{endDateLabel}</FieldLabel>}
                    error={endDateError}
                >
                    <Controller
                        name={'endDate'}
                        control={control}
                        render={({ field }) => (
                            <DateInput
                                value={field.value ?? undefined}
                                onChange={(date) => field.onChange(date ? toIsoDate(date) : null)}
                                disabledDates={
                                    startDay ? [{ before: startDay }, afterToday] : afterToday
                                }
                                lang={toDateInputLang(i18n.language)}
                                clearable
                                idleBorderClassName={GROWTH_FIELD_BORDER}
                                portalPanel
                                placeholder={endDateLabel}
                                ariaLabel={endDateLabel}
                                dataTestId={`${dataTestId}-end-date`}
                            />
                        )}
                    />
                </CareerStepFormField>
            )}
        </div>
    );
};

const FieldLabel = ({ children }: { children: string }) => (
    <Text
        size={'small'}
        color={Colors.NAVY}
        weight={'medium'}
    >
        {children}
    </Text>
);
